import React, { useEffect, useRef, useState } from "react";
import { X, ChevronLeft, ChevronRight, Download, Instagram, Facebook, Linkedin, Play } from "lucide-react";
import { KM, IMAGES, STAGED, STAGED_OFFSETS, LISTINGS, LISTING_TABS } from "../../data/km";
import { scrollTo } from "./KMHeader";
import ReadMore from "./ReadMore";

export function Hero({ onConnect }) {
  return (
    <section className="relative h-[100svh] min-h-[600px] pt-[76px] md:pt-[84px]" data-testid="km-hero">
      <div className="absolute inset-0 top-[76px] md:top-[84px] overflow-hidden">
        <img src={IMAGES.hero} alt="Styled living room with burl wood coffee table" className="w-full h-full object-cover object-[45%_50%] md:object-[50%_30%] lg:object-[50%_18%] km-hero-img" />
        <div className="absolute inset-0 bg-black/35" />
      </div>
      <div className="relative h-full max-w-[1180px] mx-auto px-6 md:px-12 lg:px-16 flex flex-col justify-end pb-14 sm:pb-20 text-white">
        <div className="mb-6 sm:mb-8 km-fade" style={{ animationDelay: ".1s" }} data-testid="hero-name">
          <h1 className="km-serif text-[26px] sm:text-[32px] lg:text-[36px] leading-none">{KM.name}<span className="sr-only">, Dallas–Fort Worth Luxury Real Estate Advisor with eXp Luxury</span></h1>
          <p className="km-eyebrow text-[10px] sm:text-[11px] lg:text-[12px] mt-3 text-white/90">{KM.tagline}</p>
        </div>
        <p className="km-serif text-[52px] leading-[0.98] sm:text-[80px] lg:text-[112px] lg:leading-[0.95] mb-8 sm:mb-10 km-fade" style={{ animationDelay: ".3s" }}>Real estate,<br /><em>curated.</em></p>
        <div className="flex flex-wrap gap-4 km-fade" style={{ animationDelay: ".5s" }}>
          <button onClick={() => scrollTo("consultations")} className="km-btn-light" data-testid="hero-buying-btn">I'm Buying</button>
          <button onClick={() => scrollTo("consultations")} className="km-btn-ghost" data-testid="hero-selling-btn">I'm Selling</button>
        </div>
      </div>
    </section>
  );
}

export function Perspective() {
  const [showPlayer, setShowPlayer] = useState(false);
  return (
    <section className="pt-14 pb-16 sm:pt-32 sm:pb-36 lg:pt-44 lg:pb-52 px-6 md:px-12 lg:px-16 bg-white" data-testid="km-perspective">
      <div className="max-w-[1180px] mx-auto reveal grid lg:grid-cols-[minmax(0,660px)_minmax(0,400px)] lg:justify-between gap-12 sm:gap-16 lg:gap-20 items-start">
        <div className="max-w-[660px] mx-auto lg:mx-0 w-full text-left" data-testid="love-note">
          <p className="km-eyebrow text-[11px] sm:text-[12px] mb-5 sm:mb-10 text-[#2E4A3B]">A Welcome Knote</p>
          <h2 className="km-serif italic font-light text-[44px] sm:text-[64px] lg:text-[81px] leading-[1.05] text-[#111111] mb-5 sm:mb-12" style={{ fontWeight: 300 }} data-testid="love-note-heading">Dear you,</h2>
          <ReadMore height="max-sm:max-h-[10.5em]" className="km-serif text-[19px] sm:text-[21px] lg:text-[25px] leading-[1.6] text-[#1A1A1A] space-y-[1.6em]" testid="perspective-body">
            <p>Wherever you are on your home quest (it takes a call and a tribe!), I'm so glad you're here.</p>
            <p>I have an eye for place and a heart for people. Positioning is my strength, and I love nothing more than seeing what a space could be and helping everyone else see it too. But before the design, the staging, the price, or the plan, I want to know you. That's where every decision begins.</p>
            <p>So tell me what you're dreaming about. I'll listen. I'll tell you the truth. And I'll be in your corner from our first conversation to the day you give and/or get your keys.</p>
          </ReadMore>
          <div className="mt-6 sm:mt-[1.6em] text-[19px] sm:text-[21px] lg:text-[25px]" data-testid="love-note-signoff">
            <p className="km-serif leading-[1.6] text-[#1A1A1A]">Much love,</p>
            <p className="km-script text-[54px] sm:text-[64px] lg:text-[86px] leading-[1] text-[#111111] mt-1" data-testid="love-note-signature">Kristin</p>
          </div>
        </div>
        <aside className="w-full max-w-[460px] mx-auto lg:mx-0 lg:sticky lg:top-[120px] lg:mt-[92px]" data-testid="playlist">
          <p className="km-eyebrow text-[11px] sm:text-[12px] text-[#2E4A3B] mb-2 sm:mb-3">Set the Mood</p>
          <p className="km-serif italic text-[22px] sm:text-[28px] lg:text-[32px] leading-tight text-[#111111] mb-4 sm:mb-6">Press play while you browse.</p>
          {/* Phones: slim card that expands into the full player on tap */}
          <button onClick={() => setShowPlayer(!showPlayer)} aria-expanded={showPlayer}
            className="sm:hidden w-full flex items-center gap-4 bg-[#F4F3EF] px-4 py-4 text-left active:opacity-80" data-testid="playlist-toggle">
            <span className="w-11 h-11 shrink-0 rounded-full bg-[#141414] text-white flex items-center justify-center">
              {showPlayer ? <X size={18} strokeWidth={1.5} /> : <Play size={18} strokeWidth={1.5} className="ml-0.5" />}
            </span>
            <span className="flex-1 min-w-0">
              <span className="block km-serif text-[20px] leading-tight text-[#111111]">Dinner Par-Tea</span>
              <span className="block km-eyebrow text-[10px] text-[#141414]/70 mt-1">{showPlayer ? "Hide player" : "Apple Music · Tap to listen"}</span>
            </span>
          </button>
          <div className={`bg-[#F4F3EF] p-1.5 sm:p-3 ${showPlayer ? "block mt-2 sm:mt-0" : "hidden"} sm:block`}>
            <iframe
              title="Dinner Par-Tea playlist on Apple Music"
              src="https://embed.music.apple.com/us/playlist/dinner-par-tea/pl.u-b3b8RX3CLmkg1g?theme=light"
              allow="autoplay *; encrypted-media *; fullscreen *; clipboard-write"
              sandbox="allow-forms allow-popups allow-same-origin allow-scripts allow-storage-access-by-user-activation allow-top-navigation-by-user-activation"
              loading="lazy"
              className="block w-full h-[450px] border-0 overflow-hidden bg-transparent"
              data-testid="playlist-embed"
            />
          </div>
          <a href="https://music.apple.com/us/playlist/dinner-par-tea/pl.u-b3b8RX3CLmkg1g" target="_blank" rel="noopener noreferrer" className="inline-block km-eyebrow text-[10px] text-[#141414] border-b border-[#141414] pt-3 pb-1 sm:pt-0 sm:pb-0.5 mt-1 sm:mt-5 hover:opacity-60 transition-opacity" data-testid="playlist-open-link">Open in Apple Music</a>
        </aside>
      </div>
    </section>
  );
}

export function About({ onConnect }) {
  return (
    <section id="about" className="pb-16 sm:pb-28 md:pb-36 pt-0 px-6 md:px-12 lg:px-16 bg-white scroll-mt-[84px]" data-testid="km-about">
      <div className="max-w-[1180px] mx-auto grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-20 items-start">
        <div className="reveal overflow-hidden w-full max-w-[560px] mx-auto lg:max-w-none"><img src={IMAGES.portrait} alt="Kristin McIntyre" className="block w-full h-auto max-sm:h-[440px] max-sm:object-cover max-sm:object-top km-zoom" /></div>
        <div className="reveal">
          <p className="km-eyebrow text-[11px] lg:text-[12.5px] mb-4 sm:mb-6 text-[#2C4A3B]">Meet Kristin</p>
          <h2 className="km-serif text-[30px] sm:text-4xl md:text-5xl leading-[1.15] mb-5 sm:mb-8">Kristin McIntyre has always been a curator. <em>Of looks, of rooms, of moments.</em></h2>
          <ReadMore height="max-sm:max-h-[13em]" className="km-body text-[#141414] space-y-5" testid="about-body">
            <p>Before real estate, she was styling wardrobes and building her own beauty brand. Those chapters taught her one thing: presentation changes how people feel. And how people feel is everything.</p>
            <p>For more than ten years, that creative work ran alongside a career in customer success. That's where she learned that people won't remember every detail of a process. They'll remember how they were made to feel inside of it.</p>
            <p>Real estate is where it all finally came together. And she didn't stop curating. Kristin added interior design and staging to her work so her clients get more than a transaction. They get a home that's positioned to shine and a vision for what it can become.</p>
            <p>She brings a designer's eye, a strategist's mind, and a whole lot of heart. She positions homes with intention, because positioning is her strength. And she helps buyers see the possibility, the value, and the place that actually feels like theirs.</p>
            <p>Through all of it, her clients come first. She listens. She tells the truth. And she makes sure every client feels seen, protected, and celebrated from start to close.</p>
          </ReadMore>
          <p className="km-serif italic text-[22px] sm:text-[24px] lg:text-[28px] leading-[1.35] text-[#2C4A3B] mt-6 mb-6 sm:mt-8 sm:mb-8" data-testid="about-motto">{KM.motto}</p>
          <button onClick={() => onConnect()} className="km-btn-dark" data-testid="about-connect-btn">Let's Connect</button>
        </div>
      </div>
    </section>
  );
}

export function Staged() {
  const flat = STAGED.flat();
  const [idx, setIdx] = useState(null);
  const open = (img) => setIdx(flat.indexOf(img));
  const step = (d) => setIdx((i) => (i + d + flat.length) % flat.length);
  return (
    <section id="staged" className="py-14 sm:py-24 md:py-32 lg:py-36 px-6 md:px-12 lg:px-16 bg-[#F4F3EF] scroll-mt-[84px]" data-testid="km-staged">
      <div className="text-center mb-8 sm:mb-16 reveal">
        <p className="km-eyebrow text-[11px] lg:text-[12.5px] mb-3 sm:mb-6 text-[#2C4A3B]">Portfolio</p>
        <h2 className="km-serif italic text-[76px] sm:text-[140px] lg:text-[180px] leading-[0.95] text-[#111111]" data-testid="staged-heading">Stag’d</h2>
      </div>
      {/* Phones: compact two-column masonry so the gallery isn't endlessly long */}
      <div className="sm:hidden columns-2 gap-3" data-testid="staged-mobile-grid">
        {flat.map((img, i) => (
          <button key={i} onClick={() => setIdx(i)} className="block w-full mb-3 break-inside-avoid overflow-hidden" data-testid={`staged-m-${i}`}>
            <img src={img.src} alt={`${img.caption} staged by Kristin McIntyre`} className="block w-full h-auto" loading="lazy" />
          </button>
        ))}
      </div>
      <div className="hidden max-w-[1240px] mx-auto sm:grid grid-cols-3 gap-4 sm:gap-5 lg:gap-6 items-start">
        {STAGED.map((col, ci) => (
          <div key={ci} className="flex flex-col gap-4 sm:gap-5 lg:gap-6">
            {STAGED_OFFSETS[ci] > 0 && <div className="hidden sm:block" style={{ aspectRatio: `1 / ${STAGED_OFFSETS[ci]}` }} aria-hidden="true" />}
            {col.map((img, i) => (
              <button key={i} onClick={() => open(img)} className="group relative overflow-hidden reveal block w-full" data-testid={`staged-img-${ci}-${i}`}>
                <img src={img.src} alt={`${img.caption} staged by Kristin McIntyre`} className="block w-full h-auto km-zoom" loading="lazy" />
                
              </button>
            ))}
          </div>
        ))}
      </div>
      {idx !== null && (
        <div className="fixed inset-0 z-[80] bg-black/90 flex items-center justify-center p-6 km-fade-fast" onClick={() => setIdx(null)} data-testid="staged-lightbox">
          <button className="absolute top-6 right-6 text-white" aria-label="Close" data-testid="lightbox-close"><X size={28} strokeWidth={1} /></button>
          <button className="absolute left-4 md:left-10 text-white p-2" onClick={(e) => { e.stopPropagation(); step(-1); }} aria-label="Previous" data-testid="lightbox-prev"><ChevronLeft size={34} strokeWidth={1} /></button>
          <figure onClick={(e) => e.stopPropagation()} className="max-w-5xl">
            <img src={flat[idx].src} alt={flat[idx].caption} className="max-h-[80vh] w-auto mx-auto object-contain" />
            <figcaption className="km-eyebrow text-[10px] text-white/70 text-center mt-4">{idx + 1} / {flat.length}</figcaption>
          </figure>
          <button className="absolute right-4 md:right-10 text-white p-2" onClick={(e) => { e.stopPropagation(); step(1); }} aria-label="Next" data-testid="lightbox-next"><ChevronRight size={34} strokeWidth={1} /></button>
        </div>
      )}
    </section>
  );
}

const fmt = (n) => `$${n.toLocaleString()}`;

function ListingCard({ l }) {
  const sold = l.status === "Sold";
  const meta = [`${l.city}, TX`, fmt(l.price), l.beds && `${l.beds} bd`, l.baths && `${l.baths} ba`, l.sqft && `${l.sqft.toLocaleString()} sq ft`].filter(Boolean).join(" \u00b7 ");
  return (
    <article className="group reveal cursor-pointer" data-testid={`listing-${l.id}`}>
      <div className={`relative overflow-hidden mb-4 ${l.img ? "" : "aspect-[4/3] bg-[#EBEAE7] flex items-center justify-center"}`}>
        {l.img ? (
          <img src={l.img} alt={`${l.address}, ${l.city}`} className="block w-full h-auto km-zoom" loading="lazy" />
        ) : (
          <span className="km-eyebrow text-[10px] text-[#141414] group-hover:scale-110 transition-transform duration-700">Listing Photo</span>
        )}
      </div>
      <span className={`inline-block km-eyebrow text-[10px] px-2.5 py-1.5 mb-4 ${sold ? "bg-[#141414] text-white border border-[#141414]" : "border border-[#141414] text-[#141414]"}`} data-testid={`listing-status-${l.id}`}>{l.status}</span>
      <h3 className="km-serif text-[26px] sm:text-[28px] leading-tight text-[#141414] group-hover:italic" data-testid={`listing-address-${l.id}`}>{l.address}</h3>
      <p className="text-[13px] tracking-[0.04em] text-[#141414] mt-2" data-testid={`listing-meta-${l.id}`}>{meta}</p>
      {l.caseStudy && <a href={l.caseStudy} download className="inline-flex items-center gap-1.5 km-eyebrow text-[10px] text-[#141414] border-b border-[#2C4A3B] pt-3 pb-1 mt-1 hover:text-[#2C4A3B] transition-colors" data-testid={`listing-case-${l.id}`}>Download the case study <Download size={12} strokeWidth={1.5} /></a>}
    </article>
  );
}

export function Listings() {
  const list = LISTINGS;
  const track = useRef(null);
  const [active, setActive] = useState(0);
  const [edges, setEdges] = useState({ start: true, end: false });

  const update = () => {
    const el = track.current;
    if (!el) return;
    const card = el.firstElementChild;
    const step = card ? card.getBoundingClientRect().width + 28 : el.clientWidth;
    setActive(Math.min(list.length - 1, Math.round(el.scrollLeft / step)));
    setEdges({ start: el.scrollLeft <= 4, end: el.scrollLeft + el.clientWidth >= el.scrollWidth - 4 });
  };
  useEffect(() => {
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const go = (d) => {
    const el = track.current;
    const card = el?.firstElementChild;
    if (!el || !card) return;
    el.scrollBy({ left: d * (card.getBoundingClientRect().width + 28), behavior: "smooth" });
  };
  const arrow = "w-12 h-12 sm:w-14 sm:h-14 rounded-full border border-[#141414] flex items-center justify-center transition-colors hover:bg-[#141414] hover:text-white disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-[#141414] disabled:cursor-not-allowed";

  return (
    <section id="listings" className="py-14 sm:py-24 md:py-32 lg:py-36 bg-white scroll-mt-[84px] border-b border-black/5 overflow-hidden" data-testid="km-listings">
      <div className="max-w-[1180px] mx-auto px-6 md:px-12 lg:px-0">
        <div className="reveal">
          <p className="km-eyebrow text-[11px] lg:text-[12.5px] mb-4 sm:mb-6 text-[#2C4A3B]">Exclusive</p>
          <h2 className="km-serif text-[40px] sm:text-5xl lg:text-[64px] lg:leading-[1.08] mb-6 sm:mb-10">Featured <em>Listings</em></h2>
        </div>
        <div className="flex items-center gap-6 mb-8 sm:mb-12">
          {LISTING_TABS.map((t) => (
            <span key={t} className="km-eyebrow text-[11px] lg:text-[12.5px] text-[#141414]" data-testid="listings-label">{t}</span>
          ))}
          <span className="flex-1 h-px bg-black/10" />
          <div className="flex items-center gap-3 sm:gap-4">
            <button onClick={() => go(-1)} disabled={edges.start} className={arrow} aria-label="Previous listing" data-testid="listings-prev"><ChevronLeft size={18} strokeWidth={1.2} /></button>
            <span className="km-eyebrow text-[11px] lg:text-[12.5px] text-[#141414] hidden sm:inline" data-testid="listings-counter">{String(active + 1).padStart(2, "0")} / {String(list.length).padStart(2, "0")}</span>
            <button onClick={() => go(1)} disabled={edges.end} className={arrow} aria-label="Next listing" data-testid="listings-next"><ChevronRight size={18} strokeWidth={1.2} /></button>
          </div>
        </div>
        <div
          ref={track}
          onScroll={update}
          className="flex gap-7 overflow-x-auto snap-x snap-mandatory no-scrollbar -mx-6 px-6 md:-mx-12 md:px-12 lg:mx-0 lg:px-0 scroll-px-6 md:scroll-px-12 lg:scroll-px-0"
          data-testid="listings-carousel"
        >
          {list.map((l) => (
            <div key={l.id} className="snap-start shrink-0 basis-[86%] sm:basis-[60%] lg:basis-[calc((100%-56px)/2.4)]">
              <ListingCard l={l} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function Consultations({ onConnect }) {
  return (
    <section id="consultations" className="py-14 sm:py-24 md:py-32 lg:py-36 px-6 md:px-12 lg:px-16 bg-white scroll-mt-[84px]" data-testid="km-consultations">
      <div className="max-w-[1180px] mx-auto grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-20 items-center">
        <div className="reveal overflow-hidden w-full max-w-[560px] mx-auto lg:max-w-none"><img src={IMAGES.consult} alt="Celebration tray with sparkling cider and gold-rimmed flutes" className="block w-full h-auto max-sm:h-[300px] max-sm:object-cover km-zoom" /></div>
        <div className="reveal">
          <p className="km-eyebrow text-[11px] lg:text-[12.5px] mb-4 sm:mb-6 text-[#2C4A3B]">Book an Appointment</p>
          <h2 className="km-serif text-[40px] sm:text-5xl lg:text-[64px] lg:leading-[1.08] leading-[1.05] mb-4 sm:mb-6">Buyer &amp; Seller<br /><em>Consultations</em></h2>
          <p className="km-eyebrow text-[11px] lg:text-[12.5px] mb-4 sm:mb-5">Style. Storytelling. Strategy.</p>
          <p className="km-serif italic text-[20px] sm:text-[24px] lg:text-[28px] leading-[1.35] text-[#141414] leading-snug mb-5 sm:mb-6">Buying? Selling? Somewhere in between?<br />Every home has a story. So does every client.</p>
          <ReadMore height="max-sm:max-h-[7.5em]" wrapClass="mb-7 sm:mb-10" className="km-body text-[#141414] space-y-4" testid="consult-body">
            <p>For sellers, Kristin uncovers what makes your home special and positions it so buyers feel it right away. For buyers, she looks past the listing photos to find the place that actually fits how you live.</p>
            <p>One conversation and you'll know your options, your timing, and your next move.</p>
          </ReadMore>
          <div className="flex flex-wrap gap-4">
            <button onClick={() => onConnect("Buying")} className="km-btn-dark" data-testid="consult-buyers-btn">For Buyers</button>
            <button onClick={() => onConnect("Selling")} className="km-btn-outline" data-testid="consult-sellers-btn">For Sellers</button>
          </div>
        </div>
      </div>
    </section>
  );
}

export function HomeFeels({ onConnect }) {
  return (
    <section className="py-14 sm:py-24 md:py-32 lg:py-36 px-6 md:px-12 lg:px-16 bg-[#F4F3EF]" data-testid="km-home-feels">
      <div className="max-w-[1180px] mx-auto grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-20 items-center">
        <div className="reveal order-2 lg:order-1">
          <p className="km-eyebrow text-[11px] lg:text-[12.5px] mb-4 sm:mb-6 text-[#2C4A3B]">Staging &amp; Design</p>
          <h2 className="km-serif text-[40px] sm:text-5xl lg:text-[64px] lg:leading-[1.08] mb-5 sm:mb-8">Home feels <em>like...</em></h2>
          <ReadMore height="max-sm:max-h-[9em]" wrapClass="mb-7 sm:mb-10" className="km-body text-[#141414] space-y-4" testid="home-feels-body">
            <p>Most people decide how they feel about a home within a few minutes of walking in. They might not be able to say why, but they know.</p>
            <p>Kristin stages with that in mind. How easily you move through a room, where you'd sit, whether the space feels calm or crowded. Those are the things buyers remember.</p>
            <p>She stages her own listings and also offers staging and design consultations, whether you're getting ready to sell or just want a fresh perspective on your space.</p>
          </ReadMore>
          <button onClick={() => onConnect("Staging & Design")} className="km-btn-dark" data-testid="elevate-space-btn">Elevate Your Space</button>
        </div>
        <div className="reveal order-1 lg:order-2 overflow-hidden w-full max-w-[560px] mx-auto lg:max-w-none"><img src={IMAGES.homeFeels} alt="Black carved bowl on stacked design books" className="block w-full h-auto max-sm:h-[300px] max-sm:object-cover km-zoom" /></div>
      </div>
    </section>
  );
}

function SocialBox({ href, label, children, testid }) {
  return (
    <a href={href} target={href === "#" ? undefined : "_blank"} rel="noopener noreferrer" aria-label={label}
      className="w-14 h-14 sm:w-[60px] sm:h-[60px] border border-white/40 flex items-center justify-center text-white hover:bg-white hover:text-[#141414] transition-colors duration-300"
      data-testid={testid}>
      {children}
    </a>
  );
}

export function Footer() {
  const link = "hover:text-white transition-colors";
  return (
    <footer className="bg-[#141414] text-white px-6 md:px-12 lg:px-16 pt-14 sm:pt-20 md:pt-28 pb-8 sm:pb-10" data-testid="km-footer">
      <div className="max-w-[1320px] mx-auto">
        <div className="grid grid-cols-2 gap-x-6 gap-y-10 sm:gap-14 lg:gap-10 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-[1.6fr_1fr_1fr] pb-10 sm:pb-16 md:pb-20">
          {/* Brand + contact */}
          <div className="col-span-2 sm:col-span-1" data-testid="footer-brand">
            <p className="text-[24px] sm:text-[30px] lg:text-[34px] uppercase tracking-[0.22em] font-light leading-tight">{KM.name}</p>
            <p className="km-eyebrow text-[10px] sm:text-[12px] tracking-[0.42em] text-white/75 mt-3 sm:mt-5">{KM.title}</p>
            <div className="mt-5 sm:mt-8 sm:space-y-3 text-[15px] sm:text-[16px] text-white/90">
              <a href={`mailto:${KM.email}`} className={`block break-all sm:py-1.5 max-sm:flex max-sm:items-center max-sm:min-h-[44px] ${link}`} data-testid="footer-email">{KM.email}</a>
              <a href={`tel:${KM.phone}`} className={`block sm:py-1.5 max-sm:flex max-sm:items-center max-sm:min-h-[44px] ${link}`} data-testid="footer-phone">Phone: {KM.phone}</a>
            </div>
            <div className="flex gap-3 sm:gap-4 mt-5 sm:mt-10">
              <SocialBox href={KM.instagramUrl} label="Instagram" testid="footer-instagram"><Instagram size={20} strokeWidth={1.4} /></SocialBox>
              <SocialBox href={KM.facebookUrl} label="Facebook" testid="footer-facebook"><Facebook size={20} strokeWidth={1.4} /></SocialBox>
              <SocialBox href={KM.linkedinUrl} label="LinkedIn" testid="footer-linkedin"><Linkedin size={20} strokeWidth={1.4} /></SocialBox>
            </div>
          </div>

          {/* Office */}
          <div data-testid="footer-office">
            <p className="km-eyebrow text-[10px] sm:text-[11px] tracking-[0.35em] text-white/55 mb-3 sm:mb-7">Office</p>
            <address className="not-italic text-[13px] sm:text-[17px] leading-[1.7] sm:leading-[1.75] text-white/90">
              {KM.office.map((l) => <span key={l} className="block">{l}</span>)}
            </address>
            <p className="text-[13px] sm:text-[16px] text-white/65 mt-3 sm:mt-6">{KM.brokerage}</p>
          </div>

          {/* Compliance */}
          <div data-testid="footer-compliance">
            <p className="km-eyebrow text-[10px] sm:text-[11px] tracking-[0.35em] text-white/55 mb-1 sm:mb-7">Compliance</p>
            <ul className="sm:space-y-3 text-[12.5px] sm:text-[15px] leading-[1.5] sm:leading-[1.7] text-white/85">
              <li><a href="https://www.trec.texas.gov/forms/consumer-protection-notice" target="_blank" rel="noopener noreferrer" className={`inline-block py-2 sm:py-1.5 ${link}`}>Texas Real Estate Commission Consumer Protection Notice</a></li>
              <li><a href="https://www.trec.texas.gov/forms/information-about-brokerage-services" target="_blank" rel="noopener noreferrer" className={`inline-block py-2 sm:py-1.5 ${link}`}>Texas Real Estate Commission Information About Brokerage Services</a></li>
              <li><a href="https://www.trec.texas.gov/" target="_blank" rel="noopener noreferrer" className={`inline-block py-2 sm:py-1.5 ${link}`}>{KM.trec}</a></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/15 pt-8 sm:pt-10 flex flex-col-reverse md:flex-row md:items-center justify-between gap-6 sm:gap-8">
          <p className="text-[12.5px] sm:text-[13px] leading-relaxed text-white/55 max-w-[720px]">Information on this site is believed to be accurate but is not guaranteed and should be independently verified. Equal Housing Opportunity.</p>
          <img src={IMAGES.logoWhite} alt="eXp Realty Luxury" className="h-9 sm:h-11 w-auto self-start md:self-auto" data-testid="km-footer-exp-logo" />
        </div>
        <p className="text-[12.5px] sm:text-[13px] text-white/45 mt-6 sm:mt-10" data-testid="footer-copyright">© {new Date().getFullYear()} {KM.name} · Texas</p>
      </div>
    </footer>
  );
}
